import { PageEvents } from '@griply/types';
import { useUpdateUserSettings } from 'features/user';
import React, { useCallback } from 'react';
import { ResizableContainer } from 'shared/components/ui/resizable-container';
import { useKeysDown } from 'shared/hooks/use-keys-down';
import { useTrackPageVisitEvent } from 'shared/hooks/use-track-page-visit-event';
import { useUser } from 'shared/hooks/use-user';
import { targetIsInput } from 'shared/utils/target-is-input';

import { GroupedList } from './components/grouped-list';
import { MobileAddTaskButton } from './components/mobile-add-task-button';
import { SidePanel } from './components/side-panel';
import * as Styled from './today.style';

const initialSidePanelWidth = 288;

export const Today: React.FC = () => {
  useTrackPageVisitEvent(PageEvents.Today, undefined);
  const user = useUser();
  const updateUserSettings = useUpdateUserSettings();

  const sidePanelOpen = user?.settings?.today?.sidePanelOpen ?? true;

  const toggleSidePanelOpen = useCallback(
    () => updateUserSettings({ today: { sidePanelOpen: !sidePanelOpen } }),
    [sidePanelOpen, updateUserSettings],
  );

  const setSidePanelWidth = useCallback(
    (width: number) => updateUserSettings({ today: { sidePanelWidth: width } }),
    [updateUserSettings],
  );

  useKeysDown(
    ['KeyC'],
    (e) => {
      if (targetIsInput(e.target)) {
        return;
      }

      toggleSidePanelOpen();
    },
    {
      otherKeysInCombinationAllowed: true,
    },
  );

  return (
    <Styled.Container>
      <Styled.Main>
        <Styled.MainContent>
          <GroupedList
            onCalendar={toggleSidePanelOpen}
            calendarOpen={sidePanelOpen}
          />
        </Styled.MainContent>
      </Styled.Main>

      <Styled.SidePanel $open={sidePanelOpen}>
        <Styled.SidePanelContent $open={sidePanelOpen}>
          <Styled.SidePanelContentClip>
            <ResizableContainer
              open={sidePanelOpen}
              resizeHandles="w"
              onWidthChange={setSidePanelWidth}
              initialWidth={
                user?.settings?.today?.sidePanelWidth ?? initialSidePanelWidth
              }
              smoothTransition
            >
              {sidePanelOpen && <SidePanel />}
            </ResizableContainer>
          </Styled.SidePanelContentClip>
        </Styled.SidePanelContent>
      </Styled.SidePanel>

      <MobileAddTaskButton />
    </Styled.Container>
  );
};
